import { useCallback, useRef, useState } from 'react';

export const useBarcodeList = <T>(getCode: (item: T) => string, initialItems?: () => T[]) => {
  const getCodeRef = useRef(getCode);

  const [items, setItems] = useState<T[]>(() => {
    const initial = initialItems?.();
    return Array.isArray(initial) ? initial : [];
  });

  const itemsRef = useRef<T[]>(items);
  const codesRef = useRef<Set<string>>(new Set(items.map((item) => getCodeRef.current(item))));

  const commit = useCallback((next: T[]) => {
    itemsRef.current = next;
    setItems(next);
  }, []);

  const has = useCallback((code: string) => codesRef.current.has(code), []);

  const prepend = useCallback(
    (item: T) => {
      codesRef.current.add(getCodeRef.current(item));
      commit([item, ...itemsRef.current]);
    },
    [commit],
  );

  const removeAt = useCallback(
    (index: number): T | undefined => {
      const current = itemsRef.current;
      const removed = current[index];
      if (removed === undefined) return undefined;
      codesRef.current.delete(getCodeRef.current(removed));
      commit(current.filter((_, i) => i !== index));
      return removed;
    },
    [commit],
  );

  const removeByCode = useCallback(
    (code: string) => {
      if (!codesRef.current.delete(code)) return;
      commit(itemsRef.current.filter((item) => getCodeRef.current(item) !== code));
    },
    [commit],
  );

  const replaceAll = useCallback(
    (next: T[]) => {
      codesRef.current = new Set(next.map((item) => getCodeRef.current(item)));
      commit(next);
    },
    [commit],
  );

  const reset = useCallback(() => {
    codesRef.current = new Set<string>();
    commit([]);
  }, [commit]);

  return { items, itemsRef, has, prepend, removeAt, removeByCode, replaceAll, reset };
};
